import { Injectable, OnDestroy } from '@angular/core';
import { DefaultUrlSerializer, NavigationEnd, Params, Router } from '@angular/router';
import { BehaviorSubject, Subject, filter, distinctUntilChanged, map } from 'rxjs';

import { DeviceUiRoute } from '@shure/device-ui/shared/services';

export type ParsedUrl = {
	url: string[];
	queryParams: Params;
	deviceId: string | null;
	roomId: string | null;
};

@Injectable({ providedIn: 'root' })
export class RouterService implements OnDestroy {
	private readonly urlSerializer = new DefaultUrlSerializer();
	private readonly destroy$ = new Subject<void>();

	private _url$ = new BehaviorSubject<string[]>([]);
	private _parsedUrl$ = new BehaviorSubject<ParsedUrl>({
		url: [],
		queryParams: {},
		deviceId: null,
		roomId: null
	});
	private deviceUiRoute$ = new BehaviorSubject<string | null>(null);

	public readonly url$ = this._url$.asObservable();
	public readonly parsedUrl$ = this._parsedUrl$.asObservable();
	public readonly roomId$ = this.parsedUrl$.pipe(
		map((url) => url.roomId),
		distinctUntilChanged()
	);
	public readonly deviceId$ = this.parsedUrl$.pipe(map((url) => url.deviceId));

	public get url(): string[] {
		return this._url$.getValue();
	}

	public get roomId(): string | null {
		return this._parsedUrl$.getValue().roomId;
	}

	public get deviceId(): string | null {
		return this._parsedUrl$.getValue().deviceId;
	}

	public get deviceUiRoute(): string | null {
		return this.deviceUiRoute$.getValue();
	}

	constructor(private readonly router: Router) {
		this.updateParsedUrl(this.router.url);

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
			this.updateParsedUrl((<NavigationEnd>event).urlAfterRedirects);
		});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateParsedUrl(urlString: string): void {
		const parsedUrl = this.urlSerializer.parse(urlString);
		const root = parsedUrl.root;
		const queryParams = parsedUrl.queryParams;

		// eslint-disable-next-line dot-notation
		const url = root.children['primary']?.segments.map((segment) => segment.path) || [];

		this._url$.next(url);

		const roomMatch = url.join('/').match(/room\/([\w\-%=]{1,})/);
		const deviceMatch = url.join('/').match(/device\/([\w\-%=]{1,})/);
		const roomId = roomMatch && roomMatch[1] ? roomMatch[1] : null;
		const deviceId = deviceMatch && deviceMatch[1] ? deviceMatch[1] : null;
		this._parsedUrl$.next({ url, queryParams, deviceId, roomId });

		const deviceUiRoute = url[0] === 'device' ? url[url.length - 1] : null;
		if (Object.values(DeviceUiRoute).includes(<DeviceUiRoute>deviceUiRoute)) {
			this.deviceUiRoute$.next(deviceUiRoute);
		}
	}
}
